/**
 * Unsent posts and comments, kept per member so what they wrote survives a
 * closed sheet, a back swipe or the app being killed.
 *
 * Routed through authStorage, like the chat unread counts: a draft belongs to
 * whoever is signed in, so a support tab (sessionStorage) never leaves the
 * member's words in the operator's own storage, nor picks up the operator's.
 * Signing out clears localStorage, drafts included.
 */
import { readAuth, writeAuth, removeAuth } from "../services/authStorage";

const PREFIX = "adoc_draft_v1:";

// Past this, the member has moved on and a stale text would only confuse.
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Storage key for a draft, or null while any part of it is still unknown. */
export const draftKey = (...parts) =>
  parts.length > 0 &&
  parts.every((part) => part !== null && part !== undefined && part !== "")
    ? PREFIX + parts.join(":")
    : null;

const sanitizeMentions = (raw) =>
  Array.isArray(raw)
    ? raw
        .filter((m) => m && m.id != null && typeof m.nickname === "string")
        .map((m) => ({ id: m.id, nickname: m.nickname }))
    : [];

const sanitizeExtra = (raw) =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};

/**
 * @returns {{ text: string, mentions: Array<{id: number, nickname: string}>, extra: object, savedAt: number } | null}
 */
export function readDraft(key, now = Date.now()) {
  if (!key) return null;
  const raw = readAuth(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const text = typeof parsed?.text === "string" ? parsed.text : "";
    const savedAt = Number(parsed?.savedAt);

    if (
      !text.trim() ||
      !Number.isFinite(savedAt) ||
      now - savedAt > DRAFT_MAX_AGE_MS
    ) {
      removeAuth(key);
      return null;
    }

    return {
      text,
      mentions: sanitizeMentions(parsed.mentions),
      extra: sanitizeExtra(parsed.extra),
      savedAt,
    };
  } catch {
    removeAuth(key);
    return null;
  }
}

/** Saves the draft, or forgets it once the text has been emptied. */
export function writeDraft(
  key,
  { text = "", mentions = [], extra = {} } = {},
  now = Date.now(),
) {
  if (!key) return;

  if (!String(text).trim()) {
    removeAuth(key);
    return;
  }

  writeAuth(
    key,
    JSON.stringify({
      text: String(text),
      mentions: sanitizeMentions(mentions),
      extra: sanitizeExtra(extra),
      savedAt: now,
    }),
  );
}

export function removeDraft(key) {
  if (key) removeAuth(key);
}
